import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import type { Mention, SummaryOrigin } from "@/shared/types/mention";

/**
 * O resumo da IA de uma menção (2026-10-31): o que veio gravado da lista
 * ou do detalhe, e o que a pessoa gerou sob pedido. Dono único do pedido
 * de "Gerar resumo" — o card, o foco do Modo revisão, o card de
 * Justificativas e o detalhe usam este hook.
 *
 * O resumo gerado fica AQUI, local ao hook, e vence o da menção até a
 * lista recarregar com ele: assim o corpo troca na hora, sem recarregar a
 * página. Falha (inclusive 429) vira `error` no próprio componente — nunca
 * o erro da página. Trocar de menção zera tudo.
 */
export function useMentionSummary(
  mention: Pick<Mention, "id" | "summary" | "summaryOrigin">,
  token: string
) {
  const [generated, setGenerated] = useState<{ text: string; origin: SummaryOrigin } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const busy = useRef(false);

  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    setGenerated(null);
    setGenerating(false);
    setError(null);
  }, [token, mention.id]);

  const generate = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const mine = generation.current;
    setGenerating(true);
    setError(null);
    try {
      const result = await apiClient.generateMentionSummary(token, mention.id);
      if (mine === generation.current) {
        setGenerated({ text: result.summary, origin: result.summary_origin });
      }
    } catch (failure) {
      if (mine === generation.current) {
        setError(errorMessage(failure, "Não foi possível gerar o resumo."));
      }
    } finally {
      if (mine === generation.current) {
        busy.current = false;
        setGenerating(false);
      }
    }
  }, [token, mention.id]);

  const summary = generated?.text ?? mention.summary;
  const origin = generated ? generated.origin : mention.summaryOrigin;
  return { summary, origin, generating, error, generate };
}

export type MentionSummaryState = ReturnType<typeof useMentionSummary>;
